import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Col, Input, Label, Table, Badge } from "reactstrap";
import axios from "axios";
import useFullPageLoader from "../../../hooks/useFullPageLoader";

// Buscador único: localiza por # de contenedor o por # de orden de compra
// y muestra el resumen (embarque + piezas + pagos + costo por pza / OC + proveedor + total).
function BuscadorContenedorOC() {
  const { user } = useContext(AuthContext);
  const URL_EMBARQUES = process.env.REACT_APP_URL_EMBARQUES;
  const URL_ORDENES_COMPRA = process.env.REACT_APP_URL_ORDENES_COMPRA;
  const URL_PAGOS_PROVEEDORES = process.env.REACT_APP_URL_PAGOS_PROVEEDORES;
  const URL_ANTICIPOS_PROVEEDORES =
    process.env.REACT_APP_URL_ANTICIPOS_PROVEEDORES;

  const [busqueda, setBusqueda] = useState("");
  const [embarques, setEmbarques] = useState([]);
  const [ocs, setOcs] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [anticipos, setAnticipos] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const monedaMXN = (n) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
      Number(n) || 0
    );
  const monedaUSD = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
      Number(n) || 0
    );

  useEffect(() => {
    const headers = {
      Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
    };
    showLoader();
    Promise.all([
      axios.get(URL_EMBARQUES, { headers }).then((r) => r.data || []).catch(() => []),
      axios.get(URL_ORDENES_COMPRA, { headers }).then((r) => r.data || []).catch(() => []),
      axios.get(URL_PAGOS_PROVEEDORES, { headers }).then((r) => r.data || []).catch(() => []),
      axios.get(URL_ANTICIPOS_PROVEEDORES, { headers }).then((r) => r.data || []).catch(() => []),
    ])
      .then(([emb, oc, pg, ant]) => {
        setEmbarques(emb.filter((e) => e.is_active === "Si"));
        setOcs(oc.filter((o) => o.is_active === "Si"));
        setPagos(pg.filter((p) => p.is_active !== "No"));
        setAnticipos(ant.filter((a) => a.is_active === "Si"));
        hideLoader();
      })
      .catch(() => hideLoader());
  }, []);

  const term = busqueda.trim().toLowerCase();

  // Embarques que hacen match por contenedor o por # de embarque
  const embarquesMatch = useMemo(() => {
    if (!term) return [];
    return embarques
      .filter(
        (e) =>
          (e.contenedor || "").toLowerCase().includes(term) ||
          String(e.idEmbarques || "").includes(term)
      )
      .map((e) => {
        // pagos + anticipos ligados a este embarque
        const pgs = pagos.filter(
          (p) => String(p.embarques?._id || "") === String(e._id)
        );
        const ants = anticipos.filter(
          (a) => String(a.embarques?._id || "") === String(e._id)
        );
        const totalMXN =
          pgs.reduce((s, p) => s + (Number(p.totalMXN) || 0), 0) +
          ants.reduce(
            (s, a) =>
              s +
              (Number(a.montoPesos) > 0
                ? Number(a.montoPesos)
                : (Number(a.importe) || 0) * (Number(a.tipoCambio) || 0)),
            0
          );
        const pzs = Number(e.total_cantidad) || 0;
        const renglones = [
          ...pgs.map((p) => ({
            concepto: p.conceptoNombre || p.conceptosPago?.concepto || "Pago",
            montoUSD: Number(p.montoUSD) || 0,
            totalMXN: Number(p.totalMXN) || 0,
            estado: p.estado || "Simulado",
          })),
          ...ants.map((a) => ({
            concepto: "ANTICIPO",
            montoUSD: Number(a.importe) || 0,
            totalMXN:
              Number(a.montoPesos) > 0
                ? Number(a.montoPesos)
                : (Number(a.importe) || 0) * (Number(a.tipoCambio) || 0),
            estado: "Pagado",
          })),
        ];
        return {
          _id: e._id,
          idEmbarques: e.idEmbarques,
          contenedor: e.contenedor || "",
          proveedor: e.proveedores?.[0]?.nombre_comercial || "",
          naviera: e.navieras?.name || "",
          pzs,
          totalMXN,
          costoPza: pzs > 0 ? totalMXN / pzs : 0,
          renglones,
        };
      });
  }, [term, embarques, pagos, anticipos]);

  // OCs que hacen match por # de OC
  const ocsMatch = useMemo(() => {
    if (!term) return [];
    return ocs
      .filter((o) => String(o.idOrdenCompra || "").includes(term))
      .map((o) => ({
        _id: o._id,
        idOrdenCompra: o.idOrdenCompra,
        proveedor: o.proveedores?.[0]?.nombre_comercial || "",
        total: Number(o.total) || 0,
        estado: o.status || o.estado || "",
      }));
  }, [term, ocs]);

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_compras ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuCompras"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
          </Row>
          <h3 align="center">Buscador (Contenedor / Orden de Compra)</h3>
          <Row className="justify-content-center">
            <Col md={6}>
              <Label className="mr-sm-2">
                Busca por # de contenedor o # de orden de compra
              </Label>
              <Input
                type="text"
                placeholder="Ej. TCLU1234567 o 105"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                autoFocus
              />
            </Col>
          </Row>
          <br />

          {!term ? (
            <h5 align="center" style={{ color: "#64748b" }}>
              Escribe un contenedor o una orden de compra para buscar
            </h5>
          ) : (
            <>
              {/* ORDENES DE COMPRA */}
              {ocsMatch.length > 0 ? (
                <div className="mb-3">
                  <h5>Órdenes de Compra ({ocsMatch.length})</h5>
                  <Table size="sm" striped borderless>
                    <thead>
                      <tr>
                        <th>OC</th>
                        <th>Proveedor</th>
                        <th>Estado</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ocsMatch.map((o) => (
                        <tr key={o._id}>
                          <td>OC {o.idOrdenCompra}</td>
                          <td>{o.proveedor}</td>
                          <td>{o.estado}</td>
                          <td className="text-right">{monedaUSD(o.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : null}

              {/* EMBARQUES / CONTENEDORES */}
              {embarquesMatch.length > 0 ? (
                <div>
                  <h5>Contenedores / Embarques ({embarquesMatch.length})</h5>
                  {embarquesMatch.map((e) => (
                    <div
                      key={e._id}
                      className="card"
                      style={{ marginBottom: 14, padding: 12 }}
                    >
                      <Row style={{ alignItems: "center" }}>
                        <Col md={4}>
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            CONTENEDOR
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 700 }}>
                            {e.contenedor || `EMB #${e.idEmbarques}`}
                          </div>
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            EMB #{e.idEmbarques} · {e.proveedor}
                            {e.naviera ? ` · ${e.naviera}` : ""}
                          </div>
                        </Col>
                        <Col md={2} className="text-center">
                          <div style={{ fontSize: 12, color: "#64748b" }}>PZS</div>
                          <div style={{ fontWeight: 700 }}>
                            {new Intl.NumberFormat("en-US").format(e.pzs)}
                          </div>
                        </Col>
                        <Col md={3} className="text-center">
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            PAGADO/SIMULADO (MXN)
                          </div>
                          <div style={{ fontWeight: 700 }}>
                            {monedaMXN(e.totalMXN)}
                          </div>
                        </Col>
                        <Col md={3} className="text-center">
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            COSTO / PZA
                          </div>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: 18,
                              color: "#15803d",
                            }}
                          >
                            {monedaMXN(e.costoPza)}
                          </div>
                        </Col>
                      </Row>
                      {e.renglones.length > 0 ? (
                        <Table size="sm" borderless className="mt-2">
                          <thead>
                            <tr>
                              <th>Concepto</th>
                              <th>Estado</th>
                              <th className="text-right">Monto USD</th>
                              <th className="text-right">Total MXN</th>
                            </tr>
                          </thead>
                          <tbody>
                            {e.renglones.map((r, i) => (
                              <tr key={i}>
                                <td>{r.concepto}</td>
                                <td>
                                  <Badge
                                    color={
                                      r.estado === "Pagado" ? "primary" : "secondary"
                                    }
                                    pill
                                  >
                                    {r.estado}
                                  </Badge>
                                </td>
                                <td className="text-right">
                                  {monedaUSD(r.montoUSD)}
                                </td>
                                <td className="text-right">
                                  {monedaMXN(r.totalMXN)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <div
                          style={{ fontSize: 12, color: "#94a3b8" }}
                          className="mt-2"
                        >
                          Sin pagos registrados para este contenedor.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : null}

              {ocsMatch.length === 0 && embarquesMatch.length === 0 ? (
                <h5 align="center" style={{ color: "#64748b" }}>
                  Sin resultados para "{busqueda}"
                </h5>
              ) : null}
            </>
          )}
        </div>
      ) : undefined}
      {loader}
    </>
  );
}

export default BuscadorContenedorOC;
